import { useState, useCallback } from 'react';

import { contactsApi } from '@lib/apiClients';
import { microServicesRoutes } from '@constants/microServicesRoutes';
import { useSessionStore } from '@features/auth';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import useWizardStore from '../store/useWizardStore';
import { CallSessionStatus } from '../types/wizard.types';

import  { type CreateCallSessionDto, type CallSessionResponse, type ContactInterface, OngoingCallAction } from '../types/wizard.types';
import useCallSessionConfigApi from './useCallSessionConfigApi';

interface CreateSessionParams {
  scriptId: string;
  scriptTitle: string;
  speech: string;
  contactListId: string;
  voicemailId: string;
  voicemailUrl?: string;
  shuffleContacts: boolean;
  currentContactsTab: string;
  contactListName: string;
  ongoingCallAction: OngoingCallAction;
  salesforceNonce: string;
  outreachData: OutreachData;
  hubspotNonce: string;
}

interface OutreachData {
  outreachSequenceId: string;
  outreachSequenceStep: string;
  nonce: string;
}

interface CreateSessionResult {
  success: boolean;
  sessionId?: string;
  contactListId?: string;
  error?: string;
}

interface ActiveSessionResult {
  hasActiveSession: boolean;
  activeSession?: CallSessionResponse;
  error?: string;
}

interface EndSessionResult {
  success: boolean;
  error?: string;
}

interface UseWizardApi {
  createSession: (params: CreateSessionParams) => Promise<CreateSessionResult>;
  checkActiveSession: () => Promise<ActiveSessionResult>;
  endSession: (sessionId: string) => Promise<EndSessionResult>;
  isLoading: boolean;
  isCheckingSession: boolean;
  currentContactsListId: string;
  outreachNonce: string;
  hubspotNonce: string;
  salesforceNonce: string;
  isSessionStarted: boolean;
}

export default function useWizardApi (): UseWizardApi {
  const { currentUser } = useSessionStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const { createCallSessionConfig, getCallSessionConfig } = useCallSessionConfigApi();
  const {
    contactListId,
    setContactListId,
    setCurrentContactsTab,
    outreachData,
    hubspotNonce: storeHubspotNonce,
    salesforceNonce: storeSalesforceNonce,
    setHubspotNonce: setStoreHubspotNonce,
    setSalesforceNonce: setStoreSalesforceNonce,
    setOutreachData: setStoreOutreachData,
    setIsSessionStarted,
    isSessionStarted
  } = useWizardStore();

  const currentContactsListId = contactListId;
  const outreachNonce = outreachData?.nonce ?? '';
  const hubspotNonce = storeHubspotNonce;
  const salesforceNonce = storeSalesforceNonce;

  const checkActiveSession = useCallback(async (): Promise<ActiveSessionResult> => {
    if (currentUser === null) {
      return { hasActiveSession: false, error: 'User not authenticated' };
    }

    setIsCheckingSession(true);

    try {
      // Use the endpoint that filters by multiple statuses and SDR ID
      const baseUrl = `${microServicesRoutes.contacts.get.callSession.getByMultipleStatusAndSdrId}/${currentUser.userId}`;
      const url = `${baseUrl}?status=started&status=paused`;

      const response = await contactsApi.get<
        { callSession: CallSessionResponse; queueLength: number } | CallSessionResponse[]
      >(url);

      const data = response.data;

      setIsCheckingSession(false);

      // API returns { callSession: {...}, queueLength: number }
      if (data !== null && typeof data === 'object' && 'callSession' in data && data.callSession !== null) {
        const sessionData = data.callSession;
        // Merge queueLength from response root into session data
        const queueLength = 'queueLength' in data ? data.queueLength : undefined;

        // Check status for sidebar to block/unblock
        setIsSessionStarted(sessionData.status === CallSessionStatus.STARTED);

        // Fetch call session config to get ongoingCallAction
        let ongoingCallAction: OngoingCallAction = OngoingCallAction.CUT_OFF;
        const configResult = await getCallSessionConfig(sessionData.id);
        if (configResult.success && configResult.data !== undefined) {
          ongoingCallAction = configResult.data.ongoingCallAction;
        }

        return {
          hasActiveSession: true,
          activeSession: {
            ...sessionData,
            queueLength: sessionData.queueLength ?? queueLength,
            ongoingCallAction,
          },
        };
      }

      // Handle array response (multiple sessions)
      if (Array.isArray(data) && data.length > 0) {
        const sessionData = data[0];

        // Check status for sidebar block/unblock
        setIsSessionStarted(sessionData.status === CallSessionStatus.STARTED);

        // Fetch call session config to get ongoingCallAction
        let ongoingCallAction: OngoingCallAction = OngoingCallAction.CUT_OFF;
        const configResult = await getCallSessionConfig(sessionData.id);
        if (configResult.success && configResult.data !== undefined) {
          ongoingCallAction = configResult.data.ongoingCallAction;
        }

        return {
          hasActiveSession: true,
          activeSession: {
            ...sessionData,
            ongoingCallAction,
          },
        };
      }

      setIsSessionStarted(false);
      return { hasActiveSession: false };
    } catch (error: unknown) {
      // Handle 404 as no active sessions
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        setIsCheckingSession(false);
        setIsSessionStarted(false);
        return { hasActiveSession: false };
      }

      await logError(
        'Failed to check active session',
        'Wizard/useWizardApi/checkActiveSession',
        { error },
        false,
      );

      setIsCheckingSession(false);
      setIsSessionStarted(false);
      return { hasActiveSession: false, error: 'Failed to check for active sessions' };
    }
  }, [currentUser, getCallSessionConfig]);

  const endSession = useCallback(async (sessionId: string): Promise<EndSessionResult> => {
    setIsLoading(true);

    try {
      const url = `${microServicesRoutes.contacts.put.callSession.updateNest}/${sessionId}`;
      await contactsApi.put(url, { status: 'finished' });

      await logInfo(
        'Session ended successfully',
        'Wizard/useWizardApi/endSession',
        { sessionId },
        false,
      );

      setIsSessionStarted(false);
      setIsLoading(false);
      return { success: true };
    } catch (error) {
      await logError(
        'Failed to end session',
        'Wizard/useWizardApi/endSession',
        { error },
        true,
      );

      setIsLoading(false);
      const errorMessage = error instanceof Error ? error.message : 'Failed to end session';
      return { success: false, error: errorMessage };
    }
  }, []);

  const createSession = async (params: CreateSessionParams): Promise<CreateSessionResult> => {
    if (currentUser === null) {
      return { success: false, error: 'User not authenticated' };
    }

    let finalContactListId = [{ contactListId: params.contactListId, completed: false }];
    setStoreHubspotNonce(params.hubspotNonce);
    setStoreSalesforceNonce(params.salesforceNonce);
    setStoreOutreachData(params.outreachData);
    setIsLoading(true);

    try {
      if (params.currentContactsTab === 'salesforce') {
        setCurrentContactsTab('salesforce');
        setStoreHubspotNonce('');
        setStoreOutreachData({
          outreachSequenceId: '',
          outreachSequenceStep: '',
          nonce: ''
        });

        const salesforceData = {
          organizationId: currentUser.organizationId,
          title: params.contactListName,
          nonce: params.salesforceNonce,
          salesforceListId: params.contactListId,
          userId: currentUser.userId,
        };

        const salesforceContacts = await contactsApi.post<ContactInterface[]>(
          microServicesRoutes.contacts.post.contact.createSalesforce
          , salesforceData);

        if (salesforceContacts.data.length > 0) {
          finalContactListId = [{ contactListId: salesforceContacts.data[0].contactListId, completed: false }];
        }
      }

      if (params.currentContactsTab === 'outreach') {
        setCurrentContactsTab('outreach');
        setStoreSalesforceNonce('');
        setStoreHubspotNonce('');
        setStoreOutreachData(params.outreachData);

        const outreachDada = {
          organizationId: currentUser.organizationId,
          title: params.contactListName,
          outreachSequenceId: Number(params.outreachData.outreachSequenceId),
          outreachSequenceStep: Number(params.outreachData.outreachSequenceStep),
          nonce: params.outreachData.nonce,
        };

        const outreachContacts = await contactsApi.post<ContactInterface[]>(
          microServicesRoutes.contacts.post.contact.createOutreach,
          outreachDada
        );

        if (outreachContacts.data.length > 0) {
          finalContactListId = [{ contactListId: outreachContacts.data[0].contactListId, completed: false }];
        }
      }

      if (params.currentContactsTab === 'hubspot') {
        setCurrentContactsTab('hubspot');
        setStoreOutreachData({ outreachSequenceId: '', outreachSequenceStep: '', nonce: params.hubspotNonce });
        setStoreSalesforceNonce(params.hubspotNonce);
        setStoreHubspotNonce(params.hubspotNonce);

        const hubspotDada = {
          organizationId: currentUser.organizationId,
          title: params.contactListName,
          listId: Number(params.contactListId),
          nonce: params.hubspotNonce,
        };

        const hubspotContacts = await contactsApi.post<ContactInterface[]>(
          microServicesRoutes.contacts.post.contact.createHubspot,
          hubspotDada
        );

        if (hubspotContacts.data.length > 0) {
          finalContactListId = [{ contactListId: hubspotContacts.data[0].contactListId, completed: false }];
        }
      }

      const payload: CreateCallSessionDto = {
        sdrId: currentUser.userId,
        sdrStatus: 'free',
        startAt: new Date().toISOString(),
        contactList: finalContactListId,
        voiceDropsIds: [params.voicemailId],
        scriptId: params.scriptId,
        scriptTitle: params.scriptTitle,
        speech: params.speech,
        agents: [],
        status: CallSessionStatus.STARTED,
        random: params.shuffleContacts,
        action: { action: 'started', userId: currentUser.userId },
      };

      const url: string = microServicesRoutes.contacts.post.callSession.createNest;

      const response = await contactsApi.post<{ callSession: CallSessionResponse }>(url, payload);

      const sessionId = response.data?.callSession?.id;

      if (sessionId !== null) {
        setIsSessionStarted(true);
      }

      await logInfo(
        'Created call session successfully',
        'Wizard/useWizardApi/createSession',
        { sessionId, shuffleContacts: params.shuffleContacts },
        false,
      );

      const generatedId = finalContactListId[0].contactListId;
      setContactListId(generatedId);


      // Create call session config if ongoing call action is not the default (CUT_OFF)
      if (sessionId !== undefined && params.ongoingCallAction !== OngoingCallAction.CUT_OFF) {
        const configResult = await createCallSessionConfig(sessionId, params.ongoingCallAction);
        if (!configResult.success) {
          await logError(
            'Failed to create call session config, but session was created',
            'Wizard/useWizardApi/createSession',
            { sessionId, error: configResult.error },
            false,
          );
        }
      }

      setIsLoading(false);
      return {
        success: true,
        sessionId,
        contactListId: finalContactListId[0].contactListId,
      };
    } catch (error) {
      await logError(
        'Failed to create call session',
        'Wizard/useWizardApi/createSession',
        { error },
        true,
      );

      setIsLoading(false);

      const errorMessage = error instanceof Error ? error.message : 'Failed to create session';
      return { success: false, error: errorMessage };
    }
  };

  return {
    isSessionStarted,
    createSession,
    checkActiveSession,
    endSession,
    isLoading,
    isCheckingSession,
    currentContactsListId,
    outreachNonce,
    hubspotNonce,
    salesforceNonce
  };
}
